import {
    put,
    call,
    select,
    takeEvery,
    all,
    fork
} from 'redux-saga/effects';
import fp from 'lodash/fp';
import urlJoin from 'url-join';
import { push, LOCATION_CHANGE } from 'connected-react-router';
import queryString from 'query-string';
import moment from 'moment';

import * as filterHeaderSelectors from '../../webcommon/header/filterheader/selectors';
import actionFactory from '../../webcommon/header/filterheader/actions';

import * as selectors from './selectors';
import * as constants from './constants';
import * as actions from './actions';
import * as appSelectors from '../../app/selectors';
import { restructureListByType } from '../../api/adapters/filterAdapters';
import {
    filtersToSearch,
    searchToFilters,
    encodeSortingIntoUrl,
    urlToSort
} from '../../common/urlUtils';
import { getVirtualDirectory } from '../../common/helpermethods/HelperMethods';
import * as routerSelectors from '../../common/router/selectors';
import * as imMonitoringApi from '../../api/monitoringSummaryApi';
import * as configurationApi from '../../api/configurationApi';
import * as systemInfoApi from '../../api/systemInfoApi';
import * as appConstants from '../../common/constants';
import * as appAdapters from '../../api/adapters/systemInfoAdapter';
import * as appActions from '../../app/actions';

export const PAGE_ROUTE = urlJoin(getVirtualDirectory(), '/summary/monitoringsummary');

const filterHeaderId = 'MonitoringSummary';

const filterHeaderActions = actionFactory(filterHeaderId);

const testLocation = action => action.type === LOCATION_CHANGE
    && action.payload.location.pathname.toLowerCase() === PAGE_ROUTE.toLowerCase();

export function* filterChanged(forceUpdate = false) {
    const initialised = yield select(selectors.getInitialised);

    if (initialised) {
        const filters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
        const gridFiltering = yield select(selectors.getDefaultSort);
        const filterSearch = filtersToSearch(filters);
        const gridSearch = encodeSortingIntoUrl(gridFiltering);
        const forceSearch = `force=${forceUpdate}`;
        const newSearch = [filterSearch, gridSearch, forceSearch].filter(fp.identity).join('&');

        const currentSearch = yield select(routerSelectors.getSearch);
        if (!currentSearch || currentSearch !== newSearch) {
            yield put(push(`${PAGE_ROUTE}?${newSearch}`));
        }
    }
}

export function* setAvailableCsasByCounterparty(counterparties = []) {
    const potentialCsaValues = yield select(appSelectors.getMonitorCsas);
    const allowedCsas = counterparties && counterparties.length
        ? potentialCsaValues.filter(csa => counterparties.includes(csa.counterparty))
        : potentialCsaValues;

    const restructuredCsas = restructureListByType(allowedCsas);

    yield put(filterHeaderActions.setAvailableListValues('csa', restructuredCsas));

    const activeFilters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
    const csaFilterIndex = fp.findIndex(filter => filter.definition.id === 'csa', activeFilters);
    const csaFilter = activeFilters[csaFilterIndex];

    if (Array.isArray(csaFilter.value) && csaFilter.value.length) {
        const filteredCsaFilterValue = csaFilter
            .value.filter(csaId => allowedCsas.find(csa => csa.value === csaId));
        const newFilters = fp.set(`[${csaFilterIndex}].value`, filteredCsaFilterValue, activeFilters);
        const openFilter = yield select(filterHeaderSelectors.getCurrentlyOpenFilter, filterHeaderId);
        yield put(filterHeaderActions.setFilters(newFilters));
        if (openFilter) {
            yield put(filterHeaderActions.openFilter(openFilter));
        }
    }
}

export function* loadDefaultConfiguration(api) {
    try {
        const config = yield call(api.getConfiguration, 'monitoringSummary');
        if (config && config.columns) {
            yield put(actions.setDefaultConfig(config.columns));
        }
    } catch (error) {
        yield put(actions.fetchDefaultConfigFailed(error.message));
    }
}

export function* getCsas() {
    const filters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
    const counterpartyFilter = filters.find(filter => filter.definition.id === 'counterparty');
    yield call(setAvailableCsasByCounterparty, counterpartyFilter.value);
}

export function* getCounterparties() {
    const counterpartyList = yield select(appSelectors.getMonitorCounterparties);
    const restructuredCounterparty = restructureListByType(counterpartyList);
    yield put(filterHeaderActions.setAvailableListValues('counterparty', restructuredCounterparty));
}

export function* getAvailableValues(action) {
    switch (action.filterId) {
        case 'csa':
            yield call(getCsas);
            break;
        case 'counterparty':
            yield call(getCounterparties);
            break;
        default:
    }
}

export function* initialise(configApi) {
    const initialised = yield (select(selectors.getInitialised));
    if (!initialised) {
        yield fork(loadDefaultConfiguration, configApi);
        yield put(actions.setLoading());
        yield put(filterHeaderActions.setFilterDefinitions(constants.filterDefinitions));
        const { defaultFilters } = constants;
        const availableEOD = yield select(appSelectors.getAvailableEOD);
        const filters = fp.set('[0].value', availableEOD)(defaultFilters);
        const csas = yield call(systemInfoApi.csasByEodRange, { RunStartDate: availableEOD.from, RunEndDate: availableEOD.to });
        const csaList = appAdapters.createCsaList(csas);
        yield put(appActions.fetchCsaDataSucceeded(csaList));
        if (!csaList.length) {
            yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
        }
        yield put(filterHeaderActions.setDefaultFilters(filters));
        const search = yield select(routerSelectors.getSearch);
        if (!search) {
            yield put(filterHeaderActions.resetFilters());
        } else {
            const filters = searchToFilters(search, constants.filterDefinitions, constants.defaultFilters);
            const gridSort = fp.get('[0].gridSort')(urlToSort(search));
            yield put(actions.setAllGridSorts(gridSort));
            yield put(filterHeaderActions.setFilters(filters));
        }
        yield put(actions.setInitialised());
        yield call(filterChanged, true);
    }
}

export function* handleUpdate(action) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        if (action.filterId === 'runDate') {
            try {
                const EodDateStart = moment(action.value.from).format(appConstants.dateFormat);
                const EodDateEnd = moment(action.value.to).format(appConstants.dateFormat);
                const csas = yield call(systemInfoApi.csasByEodRange, { RunStartDate: EodDateStart, RunEndDate: EodDateEnd });
                const csaList = appAdapters.createCsaList(csas);
                yield put(appActions.fetchCsaDataSucceeded(csaList));
                if (!csaList.length) {
                    yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
                }
                yield call(getCsas);
                yield call(getCounterparties);
            } catch (error) {
                yield put(appActions.fetchCsaDataFailed(error.message));
            }
        }
        if (action.filterId === 'counterparty') {
            yield call(setAvailableCsasByCounterparty, action.value);
        } else if (action.type === filterHeaderActions.RESET_FILTERS) {
            const filters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
            const counterpartyFilterValue = fp.flow(
                fp.find(filter => fp.get('definition.id', filter) === 'counterparty'),
                fp.get('value')
            )(filters);
            yield call(setAvailableCsasByCounterparty, counterpartyFilterValue);
            const EodDateStart = moment(filters[0].value.from).format(appConstants.dateFormat);
            const EodDateEnd = moment(filters[0].value.to).format(appConstants.dateFormat);
            const csas = yield call(systemInfoApi.csasByEodRange, { RunStartDate: EodDateStart, RunEndDate: EodDateEnd });
            const csaList = appAdapters.createCsaList(csas);
            yield put(appActions.fetchCsaDataSucceeded(csaList));
            if (!csaList.length) {
                yield put(appActions.fetchCsaDataEmpty('The CSA list was empty for the chosen date'));
            }
            yield call(getCsas);
            yield call(getCounterparties);
        }

        yield call(filterChanged, false);
    }
}
export function* handleLocationChanged(api, action) {
    const initialised = yield (select(selectors.getInitialised));
    if (initialised) {
        if (!action.payload.location.search) {
            yield call(filterChanged);
        } else {
            try {
                const { filterDefinitions } = constants;
                const { defaultFilters } = constants;

                const filters = searchToFilters(
                    action.payload.location.search,
                    filterDefinitions,
                    defaultFilters
                );
                yield put(filterHeaderActions.setFilters(filters));
                const gridsort = fp.get('[0].gridSort')(urlToSort(action.payload.location.search));
                yield put(actions.setAllGridSorts(gridsort));
                const requestedServerFilters = fp.flow(
                    fp.filter(filter => filter.value && filter.definition.server),
                    fp.sortBy(filter => filter.definition.id),
                    JSON.stringify
                )(filters);

                const currentServerFilters = yield select(selectors.getCurrentServerFilters);
                const params = queryString.parse(action.payload.location.search);
                const force = params.force === 'true';

                if (force || (currentServerFilters !== requestedServerFilters)) {
                    yield put(actions.setLoading());
                    const data = yield call(api.imMonitoringSummaries, filters);
                    yield put(actions.fetchImSummariesSucceeded(
                        data,
                        requestedServerFilters
                    ));
                    if (data.length === 0) {
                        yield put(actions.fetchImSummariesEmpty('No CSA Summaries data present'));
                    }
                }
                const requestedClientFilters = fp.flow(
                    fp.filter(filter => filter.value && !filter.definition.server),
                    fp.sortBy(fp.get('definition.id')),
                    fp.filter(filter => !((filter.definition.id === 'status') && !filter.value.length))
                )(filters);
                yield put(actions.applyClientFilters(requestedClientFilters));
            } catch (error) {
                yield put(actions.fetchImSummariesFailed(error.message));
            }
        }
    }
}

export function* refreshMonitoringSummary(api){
    try{
        const filters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
        const requestedServerFilters = fp.flow(
            fp.filter(filter => filter.value && filter.definition.server),
            fp.sortBy(filter => filter.definition.id),
            JSON.stringify
        )(filters);
        yield put(actions.setLoading(true));
        const data = yield call(api.imMonitoringSummaries, filters);
        yield put(actions.fetchImSummariesSucceeded(
            data,
            requestedServerFilters
        ));
        if (data.length === 0) {
            yield put(actions.fetchImSummariesEmpty('No CSA Summaries data present'));
        }
        const requestedClientFilters = fp.flow(
            fp.filter(filter => filter.value && !filter.definition.server),
            fp.sortBy(filter => filter.definition.id)
        )(filters);

        // either the server filters were changed, so we need to apply client
        // filters, or the client filters changed. Either way, we always need
        // to apply the client filter.
        yield put(actions.applyClientFilters(requestedClientFilters));
    }
    catch (error){
        yield put(actions.fetchImSummariesFailed(error.message));
    } finally {
        yield put(actions.setLoading(false));
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(actions.INITIALISE, initialise, configurationApi),
        takeEvery(
            [
                filterHeaderActions.FETCH_AVAILABLE_LIST_VALUES,
                filterHeaderActions.OPEN_FILTER
            ],
            getAvailableValues
        ),
        takeEvery([
            filterHeaderActions.UPDATE_FILTER,
            filterHeaderActions.REMOVE_FILTER,
            filterHeaderActions.RESET_FILTERS
        ], handleUpdate),
        takeEvery([
            actions.REFRESH_DATA
        ], filterChanged, true),
        takeEvery(
            testLocation,
            handleLocationChanged,
            imMonitoringApi
        ),
        takeEvery(actions.SET_GRID_SORTS, filterChanged, false),
        takeEvery(actions.REFRESH_MONITORING_SUMMARY, refreshMonitoringSummary, imMonitoringApi)

    ]);
}
